import { AdminRoles, BpAdminRoles, ProfileRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTranslation } from 'react-i18next';
import type { MultiValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { getAdminRoleTranslation } from 'utils/adminRoles';
import { showSuccessSaveToast, showUserDbErrorToast } from 'utils/toasts';

const BpUpdateAclRolesMutationDocument = graphql(`
  mutation BpUpdateProfileRoles($uuid: String!, $roles: [String!]!) {
    updateAclRoles(uuid: $uuid, roles: $roles) {
      entitiesUpdated
    }
  }
`);

const PersonAdminRolesFormQuery = graphql(`
  query PersonAdminRolesFormData($organizationUuid: ID!, $personUuid: ID!) {
    profiles(where: { organization: { uuid: $organizationUuid }, uuid: $personUuid }) {
      __typename
      uuid
      organizationRoles
      selectName
    }
  }
`);

type PersonAdminRolesFormProps = {
  personUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type PersonAdminRolesFormType = {
  uuid: string;
  roles: string[];
};

export const PersonAdminRolesForm = ({ personUuid, onLoad, onClose }: PersonAdminRolesFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, updateProfileRoles] = useMutation(BpUpdateAclRolesMutationDocument);

  const [{ data }] = useQuery({
    query: PersonAdminRolesFormQuery,
    variables: { organizationUuid, personUuid },
    requestPolicy: 'network-only',
  });

  const profile = data?.profiles[0] ?? null;

  const initialValues: PersonAdminRolesFormType = {
    uuid: personUuid,
    roles: profile?.organizationRoles.filter((r) => r !== null) ?? [],
  };

  const profileRole = profile?.organizationRoles
    .filter((r) => Object.values(ProfileRoles).includes(r as ProfileRoles))
    .shift();

  async function handleSubmit(values: PersonAdminRolesFormType, helpers: FormikHelpers<PersonAdminRolesFormType>) {
    onLoad(true);

    if (profileRole) values.roles.push(profileRole);

    const { error } = await updateProfileRoles(values);

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessSaveToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  }

  const rolesOptions: SelectOptionType[] = [
    AdminRoles.ProfileAdmin,
    AdminRoles.CollaborationAdmin,
    AdminRoles.OrganizationAdmin,
    AdminRoles.GroupAdmin,
    AdminRoles.ShadowAdmin,
    BpAdminRoles.CloudAdmin,
    BpAdminRoles.ThreemaAdmin,
    BpAdminRoles.ImportAdmin,
    BpAdminRoles.SchoolOffice,
  ].map((r) => ({ label: getAdminRoleTranslation(r), value: r }));

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {({ resetForm, dirty, setFieldValue, values }) => {
        return (
          <Form>
            <Select
              name='roles'
              label={t('adminRoles.title')}
              value={rolesOptions.filter((v) => values.roles.includes(v.value as string))}
              options={rolesOptions}
              onChange={async (event) => {
                await setFieldValue(
                  'roles',
                  (event as MultiValue<SelectOptionType>).map((e) => e.value),
                );
              }}
              isMulti
              isSearchable
              menuPosition='fixed'
            />
            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: !dirty }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
